import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';
import { thesisApi } from '@/api/thesisApi';

/**
 * Plagiarism / semantic similarity check dialog.
 *
 * Scans paragraphs of the selected chapter against the project's own evidence
 * library. Flags paragraphs with cosine similarity > threshold (default 0.92)
 * as potentially-too-close-to-source.
 */
export default function PlagiarismDialog({ open, onOpenChange, userId, projectId, chapterId }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [threshold, setThreshold] = useState(0.92);

  const runCheck = async () => {
    setLoading(true);
    try {
      const data = await thesisApi.plagiarismCheck(userId, projectId, { chapterId, threshold });
      setResult(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setResult(null); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-auto" data-testid="plagiarism-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            <ShieldAlert className="w-5 h-5 text-[#1d4ed8]" />
            Originality check
          </DialogTitle>
          <DialogDescription className="text-xs">
            Scans each paragraph for semantic similarity against your project's own evidence library.
            Paragraphs that are very close to source material (cosine ≥ {threshold}) are flagged for review.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 py-3 border-b border-border">
          <label className="text-xs text-muted-foreground">Threshold</label>
          <input
            type="range" min="0.7" max="0.99" step="0.01"
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            disabled={loading}
            className="flex-1 max-w-[200px]"
            data-testid="plagiarism-threshold-slider"
          />
          <span className="font-mono text-sm w-12">{threshold.toFixed(2)}</span>
          <Button onClick={runCheck} disabled={loading} className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="plagiarism-run-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</> : 'Run check'}
          </Button>
        </div>

        {result && (
          <div className="py-4 space-y-3" data-testid="plagiarism-results">
            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat n={result.summary.paragraphs_checked} label="Paragraphs" color="text-foreground" />
              <Stat n={result.summary.flagged} label="Flagged" color={result.summary.flagged ? 'text-rose-600' : 'text-emerald-600'} />
              <Stat n={`${result.summary.threshold}`} label="Threshold" color="text-foreground" />
            </div>

            {result.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nothing to check.</p>
            ) : (
              <ul className="space-y-2.5">
                {result.rows.map((r, i) => (
                  <li key={i} className={`p-3 rounded-lg border ${
                    r.flag === 'high_similarity'
                      ? 'border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/30'
                      : r.flag === 'review'
                      ? 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30'
                      : 'border-emerald-200/50 bg-emerald-50/30 dark:border-emerald-900/30 dark:bg-emerald-950/20'
                  }`} data-testid={`plagiarism-row-${r.paragraph_index}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
                        {r.flag === 'high_similarity'
                          ? <><AlertTriangle className="w-3 h-3 text-rose-600" /><span className="text-rose-600">High similarity</span></>
                          : r.flag === 'review'
                          ? <><ShieldAlert className="w-3 h-3 text-amber-600" /><span className="text-amber-600">Worth review</span></>
                          : <><ShieldCheck className="w-3 h-3 text-emerald-600" /><span className="text-emerald-600">Original</span></>}
                      </span>
                      <span className="font-mono text-xs">cosine · {r.max_similarity}</span>
                    </div>
                    <p className="text-xs text-foreground leading-relaxed line-clamp-3">{r.preview}</p>
                    {r.matched_sources.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-rose-200/40 dark:border-rose-900/30 text-[11px]">
                        <div className="text-muted-foreground mb-1">Matches your source:</div>
                        {r.matched_sources.map((s, j) => (
                          <div key={j} className="font-mono text-muted-foreground">
                            • {s.source_title} ({s.score})
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              InkPhD does not block content. Use this scan to identify paragraphs that may need rewording for originality
              or where additional in-text citation of the underlying source would be appropriate.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ n, label, color }) {
  return (
    <div className="p-3 rounded-lg border border-border bg-muted/30 dark:bg-white/[0.02]">
      <div className={`font-serif text-2xl font-semibold ${color}`} style={{ fontFamily: '"Fraunces", Georgia, serif' }}>{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
